import gql from 'graphql-tag'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'

const initialState = {
  params: {
    msisdn: '',
    activationCode: '',
    pPlusToken: null
  },
  orderByAgent: null,
  result: {}
}

const ActivationInfoModule = {
  namespaced: true,
  state: initialState,
  getters: {
    topUpAmounts: state => {
      return state.result.details.topUpAmounts
    },
    singleTopUpAmounts: state => {
      return state.result.details.singleTopUp.amounts
    },
    msisdn: state => {
      return state.params.msisdn
    },
    activationCode: state => {
      return state.params.activationCode
    },
    pPlusToken: state => {
      return state.params.pPlusToken
    },
    hasValidatedPPlusToken: state => {
      return state.params.pPlusToken !== null
    },
    params: state => {
      return state.params
    },
    result: state => {
      return state.result
    },
    resultType: state => {
      return state.result.type
    },
    pendingActivation: state => {
      return state.result.details.webIdentPending
    },
    market: state => {
      return state.result.details.market
    },
    preactivatedTariff: state => {
      return state.result.details.preactivatedTariff
    },
    activatableTariffs: state => {
      return state.result.details.activatableTariffs
    },
    availableTariffOptions: (state, getters) => {
      const preactivatedTariff = state.result?.details?.activatableTariffs?.find(tariff => {
        return tariff.tariffId === getters.tariffIdentifier
      })
      return preactivatedTariff?.tariffOptions || state.result?.details?.activatableTariffs[0]?.tariffOptions || []
    },
    tariffOptionsForDisplay: (state, getters) => {
      const availableTariffOptions = getters.availableTariffOptions
      const organizedTariffOptions = []
      availableTariffOptions.forEach(tariffOption => {
        const calculatePrice = tariffOption.price === null ? 'kostenlos' : function () { // just some logic to create strings like '10€ pro 30 Tage
          const rawPrice = parseFloat(tariffOption.price / 100).toFixed(2).replace('.', ',') + '€'
          const unit = function () {
            if (tariffOption.duration.amount === 1) {
              return tariffOption.duration.unit === 'DAY' ? 'Tag' : 'Monat'
            } else {
              const amount = tariffOption.duration.amount
              return tariffOption.duration.unit === 'DAY' ? amount + ' Tage' : amount + ' Monate'
            }
          }
          return rawPrice + ' pro ' + unit()
        }
        organizedTariffOptions.push({
          tariffOptionId: tariffOption.tariffOptionId,
          tariffOptionName: tariffOption.tariffOptionName,
          price: calculatePrice()
        })
      })
      return organizedTariffOptions
    },
    tariffIdentifier: state => {
      return state.result?.details?.tariffIdentifier
    },
    tariffId: state => {
      return state.result?.details?.tariffId
    },
    orderByAgent: state => {
      return (state.orderByAgent !== null) ? true : null
    }
  },
  mutations: {
    storeParams (state, params) {
      state.params = {
        ...state.params,
        ...params
      }
    },
    storeResult (state, result) {
      state.result = result
    },
    keepActivationData (state) {
      Object.assign(state, initialState)
      this.commit('changeModalViewState', { modal: 'activationInfo', viewState: false }, { root: true })
    },
    collectActivationData (state, nextStep = 'personalDataInput') {
      state.view = nextStep
      this.commit('activation/goToStep', nextStep, { root: true })
      this.commit('changeModalViewState', { modal: 'activationInfo', viewState: false }, { root: true })
    },
    storeOrderByAgent: (state, orderByAgent) => {
      state.orderByAgent = orderByAgent
    }
  },
  actions: {
    async activationInfo ({ commit, state, rootGetters, getters }, { msisdn, activationCode, nextStep = 'personalDataInput' }) {
      commit('clearMessages', {}, { root: true })
      commit('storeParams', { msisdn, activationCode })
      const apolloClient = getProvider().defaultClient

      return apolloClient.mutate({
        mutation: gql`mutation activation ($activationInfoInput: ActivationInfoInput!){
          activationInfo (activationInfoInput: $activationInfoInput){
            type, details {
              contractId, market, creditBalance, subBrand, tariffIdentifier, webIdentPending
              topUpAmounts {
                autoAmounts {
                  monthly {
                    amount
                    default
                  }
                  threshold {
                  amount
                  default
                  defaultTrigger
                  trigger
                  }
                }
                directAmounts {
                  amount
                  default
                }
              }
              singleTopUp {
                amounts {
                  amount,
                  default
                }
              }
              preactivatedTariff {
                tariffIdentifier,
                name,
                basicFee,
                duration,
                { amount, unit }
              }
              activatableTariffs {
                activePromo
                advantages
                allTariffOptions {
                  automaticExtension
                  duration { amount unit formattedUnit}
                  name
                  notBookableWith
                  phoneFlat
                  price
                  formattedPrice
                  smsFlat
                  tariffoptionId
                  details
                }
                colorCode
                tariffId: tariffIdentifier
                baseTariffId
                basicFee
                formattedBasicFee
                highlightTexts
                name
                priceListUrl
                promoText
                tariffText
                tariffType
                priceDetails {
                  additional
                  name
                  text
                }
                volumeDetails {
                  amount
                  destination
                  text
                  type
                  unit
                }
                phoneFlat
                runtime { amount formattedUnit specificEndDate type unit}
                smsFlat
                tariffIdentifier
                tariffOptions { tariffOptionId tariffOptionName notBookableWith price duration { amount unit }
                }
              }
            }
          }
        }`,
        variables: {
          activationInfoInput: {
            ...getters.params,
            brand: rootGetters.brand
          }
        }
      }).then((response) => {
        commit('storeResult', response.data.activationInfo)
        if (getters.resultType !== 'OTHER_BRAND') {
          if (getters.pendingActivation === true) {
            if (getters.preactivatedTariff !== null) {
              commit('changeModalViewState', { modal: 'activationInfo', viewState: true }, { root: true })
            } else {
              commit('activation/goToStep', nextStep, { root: true })
            }
          } else {
            commit('activation/goToStep', nextStep, { root: true })
          }
        }
        commit('activation/setActivationProcess', true, { root: true })
        return getters.resultType
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
        throw error
      })
    },
    async validatePPlusToken ({ commit, state, rootGetters, getters }, { msisdn, pPlusToken }) {
      // if we already have a valid pplus token and get the same again, just return true
      if (getters.pPlusToken === pPlusToken && getters.hasValidatedPPlusToken) {
        return true
      }
      commit('clearMessages', {}, { root: true })
      const apolloClient = getProvider().defaultClient

      return apolloClient.mutate({
        mutation: gql`mutation activation ($brand: String!, $pPlusToken: String!, $msisdn: String!){
          validatePplusToken (
            brand: $brand
            id: $pPlusToken
            msisdn: $msisdn
          ){
            validatedPplusToken
          }
        }`,
        variables: {
          brand: rootGetters.brand,
          pPlusToken: pPlusToken,
          msisdn: msisdn
        }
      }).then((response) => {
        // When pplus token was successfully validated, it needs to be persistent for future use
        // futhermore we can already take the validated msisdn as given
        commit('storeParams', { msisdn, activationCode: null, pPlusToken })
        return true
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
        return false
      })
    }
  }
}

export default ActivationInfoModule
